/**
 * Channel detail — header (name, type, is_active toggle), edit config,
 * test-send panel, embedded subscriber list.
 */
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Send, Info } from 'lucide-react';
import { Breadcrumbs, BreadcrumbItem, Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Button,
  Badge,
  Skeleton,
  Switch,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Input,
} from '@vnetwork/ui/primitives';
import { StatusPill } from '@vnetwork/ui/data';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import {
  useChannel,
  useUpdateChannel,
  useTestChannel,
  useSubscribers,
  type ChannelType,
  type TestChannelResult,
  type SubscriberScope,
} from '@/api/healthcheck';
import { hcExtractError } from '@/api/healthcheck/_shared';
import { PROVIDER_META } from './channel-provider-meta';
import {
  ChannelProviderConfigForm,
  isProviderConfigValid,
  buildConfigPayload,
  seedEditConfig,
  type ProviderConfigState,
} from './ChannelProviderConfigForms';
import { TeamsOAuthPanel } from './TeamsOauthPanel';
import { TeamsModeToggle, type TeamsMode } from './TeamsModeToggle';
import { SubscriberPanel } from './SubscriberPanel';

// ---- Test result panel ---------------------------------------------------

function TestResultPanel({ result }: { result: TestChannelResult }) {
  if (result.ok) {
    return (
      <Alert tone="success" title={`Test sent successfully to ${result.total_subscribers} subscriber${result.total_subscribers === 1 ? '' : 's'}.`} />
    );
  }
  if (result.error_count > 0 && result.total_subscribers > result.error_count) {
    return (
      <Alert
        tone="warning"
        title={`Partial success — ${result.error_count} of ${result.total_subscribers} subscriber${result.total_subscribers === 1 ? '' : 's'} failed.`}
      >
        {result.errors && result.errors.length > 0 && (
          <ul style={{ margin: 0, paddingLeft: 16 }}>
            {result.errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        )}
      </Alert>
    );
  }
  return (
    <Alert
      tone="danger"
      title={`Test failed — ${result.error_count} error${result.error_count === 1 ? '' : 's'}.`}
    >
      {result.errors && result.errors.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: 16 }}>
          {result.errors.map((e, i) => <li key={i}>{e}</li>)}
        </ul>
      )}
    </Alert>
  );
}

// ---- Edit config section -------------------------------------------------

interface EditConfigProps {
  channelId: string;
  type: ChannelType;
  /** Raw masked config from BE GET — secrets will be stripped before seeding form. */
  initialConfig: Record<string, unknown>;
}

function EditConfigSection({ channelId, type, initialConfig }: EditConfigProps) {
  const meta = PROVIDER_META[type];

  // Detect initial teams mode from stored config.
  const initialTeamsMode = (): TeamsMode => {
    const raw = initialConfig['teams'] as Record<string, unknown> | undefined;
    return raw?.mode === 'oauth2' ? 'oauth2' : 'webhook';
  };

  const [teamsMode, setTeamsMode] = useState<TeamsMode>(
    type === 'teams' ? initialTeamsMode() : 'webhook',
  );

  // C1 FIX: extract provider sub-object then seed via seedEditConfig which:
  //   - sets secret fields to '' (never seeds from masked "****" strings)
  //   - also clears any non-secret field containing "****" (partial mask guard)
  const rawProviderConfig = (initialConfig[type] ?? initialConfig) as Record<string, unknown>;
  const [config, setConfig] = useState<ProviderConfigState>(
    () => seedEditConfig(type, rawProviderConfig),
  );
  const [showErrors, setShowErrors] = useState(false);

  const update = useUpdateChannel(channelId);

  const configOk = isProviderConfigValid(type, config);

  const save = () => {
    if (!configOk) { setShowErrors(true); return; }
    const payload = buildConfigPayload(type, config, true);
    update.mutate({ config: { [type]: payload } });
  };

  return (
    <Card flat>
      <CardHeader>
        <CardTitle>{meta.label} configuration</CardTitle>
      </CardHeader>
      <CardBody>
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {/* Teams-specific: mode toggle (shared with ChannelCreate) */}
          {type === 'teams' && (
            <TeamsModeToggle value={teamsMode} onChange={setTeamsMode} />
          )}

          {/* Webhook mode — standard field form */}
          {(type !== 'teams' || teamsMode === 'webhook') && (
            <>
              <ChannelProviderConfigForm
                type={type}
                config={config}
                onChange={(patch) => setConfig((c) => ({ ...c, ...patch }))}
                showErrors={showErrors}
                isEditing
              />
              {update.error && (
                <Alert tone="danger" title={hcExtractError(update.error, 'Save failed')} />
              )}
              <div>
                <Button onClick={save} disabled={update.isPending} size="sm">
                  Save configuration
                </Button>
              </div>
            </>
          )}

          {/* OAuth mode — connect panel + team/channel dropdowns */}
          {type === 'teams' && teamsMode === 'oauth2' && (
            <TeamsOAuthPanel channelId={channelId} currentConfig={initialConfig} />
          )}
        </div>
      </CardBody>
    </Card>
  );
}

// ---- Main detail page ----------------------------------------------------

export function ChannelDetail() {
  const navigate = useNavigate();
  const { projectId, channelId } = useParams<{ projectId: string; channelId: string }>();
  const base = `/services/healthcheck/${projectId}`;
  const { show } = useToast();

  const { data: ch, isLoading, isError, refetch } = useChannel(channelId);
  const update = useUpdateChannel(channelId ?? '');
  const test = useTestChannel();
  const { data: subs = [] } = useSubscribers(channelId);

  const [name, setName] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [testResult, setTestResult] = useState<TestChannelResult | null>(null);

  if (isError) {
    return (
      <Container size="xl">
        <Alert
          tone="danger"
          title="Couldn't load channel"
          actions={
            <Button size="sm" variant="secondary" onClick={() => refetch()}>Retry</Button>
          }
        >
          <button
            type="button"
            onClick={() => navigate(`${base}/notifications`)}
            style={{ background: 'none', border: 'none', padding: 0, color: 'var(--vn-primary-text)', cursor: 'pointer' }}
          >
            Back to channels
          </button>
        </Alert>
      </Container>
    );
  }

  if (isLoading || !ch) {
    return (
      <Container size="xl">
        <Skeleton style={{ height: 40, width: 280, marginBottom: 16 }} />
        <Skeleton style={{ height: 260 }} />
      </Container>
    );
  }

  const typeMeta = ch.type ? PROVIDER_META[ch.type] : null;
  const Icon = typeMeta?.icon ?? Send;
  const hasSubscribers = subs.length > 0;

  // Constrain subscriber scopes to what this channel type supports.
  // Single-type channels only accept their own type; multi-type (type=null) allow all.
  const ALL_SCOPES: SubscriberScope[] = ['telegram', 'email', 'webhook', 'teams', 'slack'];
  const allowedScopes: SubscriberScope[] = ch.type ? [ch.type as SubscriberScope] : ALL_SCOPES;

  const sendTest = () => {
    if (!channelId) return;
    setTestResult(null);
    test.mutate(channelId, {
      onSuccess: (r) => setTestResult(r),
      // H1 FIX: surface BE error.message rather than generic axios string
      onError: (e) => show({ tone: 'danger', title: hcExtractError(e, 'Test failed') }),
    });
  };

  const saveName = () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed.length > 255) return;
    update.mutate(
      { name: trimmed },
      { onSuccess: () => setEditingName(false) },
    );
  };

  const toggleActive = () => {
    update.mutate({ is_active: !ch.is_active });
  };

  return (
    <Container size="xl">
      <PageHeader
        icon={<Icon size={22} />}
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem asChild><Link to={`${base}/notifications`}>Channels</Link></BreadcrumbItem>
            <BreadcrumbItem current>{ch.name}</BreadcrumbItem>
          </Breadcrumbs>
        }
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
            {ch.name}
            {typeMeta ? (
              <Badge tone="neutral" appearance="subtle" size="sm">{typeMeta.label}</Badge>
            ) : (
              <Badge tone="neutral" appearance="subtle" size="sm">Multi-type</Badge>
            )}
          </span>
        }
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)' }}>
            <StatusPill tone={ch.is_active ? 'success' : 'muted'}>
              {ch.is_active ? 'Active' : 'Paused'}
            </StatusPill>
            <Switch
              checked={ch.is_active}
              onCheckedChange={toggleActive}
              disabled={update.isPending}
              aria-label={ch.is_active ? 'Deactivate channel' : 'Activate channel'}
            />
          </div>
        }
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        {/* Name edit */}
        <Card flat>
          <CardHeader>
            <CardTitle>Channel name</CardTitle>
          </CardHeader>
          <CardBody>
            {editingName ? (
              <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'flex-start' }}>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={255}
                  style={{ maxWidth: 340 }}
                  autoFocus
                />
                <Button size="sm" onClick={saveName} disabled={update.isPending}>Save</Button>
                <Button size="sm" variant="ghost" onClick={() => setEditingName(false)}>Cancel</Button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)' }}>
                <span style={{ fontWeight: 500 }}>{ch.name}</span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setName(ch.name); setEditingName(true); }}
                >
                  Rename
                </Button>
              </div>
            )}
          </CardBody>
        </Card>

        {/* Provider config — typed channels only */}
        {ch.type ? (
          <EditConfigSection
            channelId={ch.id}
            type={ch.type as ChannelType}
            initialConfig={ch.config}
          />
        ) : (
          /* H2 FIX: explain multi-type config editing not available in UI */
          <Card flat>
            <CardHeader>
              <CardTitle>Configuration</CardTitle>
            </CardHeader>
            <CardBody>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--vn-space-2)', color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
                <Info size={15} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>
                  This is a <strong>multi-type channel</strong> — its config holds multiple provider
                  blocks (e.g. <code>telegram</code>, <code>email</code>) nested by provider key.
                  Editing multi-type config in the UI is not yet supported. Use the API to update
                  the <code>config</code> field directly.
                </span>
              </div>
            </CardBody>
          </Card>
        )}

        {/* Test-send */}
        <Card flat>
          <CardHeader>
            <CardTitle>Test notification</CardTitle>
          </CardHeader>
          <CardBody>
            <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
              <p style={{ margin: 0, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
                Sends a test message to all subscribers on this channel.
              </p>
              <div>
                <Button
                  size="sm"
                  startIcon={<Send size={15} />}
                  variant="secondary"
                  onClick={sendTest}
                  disabled={!hasSubscribers || test.isPending}
                  title={!hasSubscribers ? 'Add a subscriber first' : undefined}
                >
                  {test.isPending ? 'Sending…' : 'Send test'}
                </Button>
                {!hasSubscribers && (
                  <span style={{ marginLeft: 'var(--vn-space-3)', color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
                    Add a subscriber first
                  </span>
                )}
              </div>
              {testResult && <TestResultPanel result={testResult} />}
            </div>
          </CardBody>
        </Card>

        {/* Subscribers */}
        <Card flat>
          <CardBody>
            <SubscriberPanel channelId={ch.id} allowedScopes={allowedScopes} />
          </CardBody>
        </Card>
      </div>
    </Container>
  );
}

